import {useEffect, useRef, useCallback} from "react";

// eslint-disable-next-line @typescript-eslint/ban-types
export function useDebounce<T extends Function>(callback: T, delay: number) {
    // Use a ref to store the function handle between rerenders
    const callbackRef = useRef(callback);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>();

    // Update the ref each render so it always gets the latest function
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const debouncedCallback = useCallback((...args: unknown[]) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            callbackRef.current(...args);
        }, delay);
    }, [delay]);

    const cancel = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    // Clear the timeout when the component is unmounted or when the delay changes
    useEffect(() => {
        return () => {
            cancel();
        };
    }, [delay]);

    return { debounce: debouncedCallback, cancel };
}